import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import api from '../../../api'
import Responsive from '../../../assets/config/Responsive'
import Colors from '../../../assets/styles/Colors'
import { Container } from '../../../assets/styles/GlobalStyle'
import Loader from '../../../components/Loader'
import BackgroundIcon from '../../../components/v2/BackgroundIcon'
import MovingPrice from '../../../components/v2/MovingPrice'
import ScreenHeader from '../../../components/v2/ScreenHeader'
import Title from '../../../components/v2/Title'
import Button from '../../../components/v2/buttons/Button'
import StartOver from '../../../components/v2/buttons/StartOver'
import PaymentCard from '../../../components/v2/cards/PaymentCard'
import BaseModal from '../../../components/v2/modals/BaseModal'
import PetProgressbar from '../../../components/v2/progrees-bars/PetProgressBar'
import { useAppSelector } from '../../../store'
import { AppState } from '../../../store/appSlice'
import { PetState, clearPetStore, setSubscriptionAgree } from '../../../store/petSlice'
import { clearAbroadStore } from '../../../store/travelAbroadSlice'
import { Helmet } from 'react-helmet-async'

const PetPayment = () => {
  //
  const dispatch = useDispatch()
  //
  const { t } = useTranslation()
  //
  const navigate = useNavigate()
  //
  const { isProduction } = useAppSelector((root: { app: AppState }) => root.app)
  //
  const [startOverModal, setStartOverModal] = useState<boolean>(false)
  //
  const [selectedPaymentMethodState, setSelectedPaymentMethodState] = useState<string | undefined>(undefined)
  //
  const { checkout, filter } = useAppSelector((data: { pet: PetState }) => data.pet)
  //
  const [getPetPaymentUrl, { data: petPaymentResponse, isLoading: isPetPaymentUrlLoading }] =
    api.usePetPaymentMutation()

  const [afterPaymentStepsOnDev, { isLoading: isAfterPaymentStepsOnDevLoading }] =
    api.useAfterPetPaymentStepsOnDevMutation()
  //
  const _getPetPaymentUrl = async () => {
    if (
      checkout.orderId &&
      selectedPaymentMethodState &&
      isProduction &&
      (filter.seletedPaymentMethod?.type === 'MONTHLY' ? checkout?.subscriptionAgree : true)
    ) {
      try {
        await getPetPaymentUrl({
          orderId: checkout.orderId,
          paymentType: 'other',
          recurrentPaymentApprove: checkout.subscriptionAgree,
        }).unwrap()
        //
      } catch (error: any) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0]
          const errorMessage = firstError.message
          toast.error(errorMessage)
        } else {
          toast.error(`${t('anErrorOccurred')} PetPayment.tsx`)
        }
      }
    }
  }
  //
  //
  const _mainPayment = useCallback(() => {
    if (petPaymentResponse && petPaymentResponse.paymentGatewayUrl) {
      // clear abroad rtk store
      dispatch(clearPetStore())
      //
      document.location.href = petPaymentResponse.paymentGatewayUrl
    }
  }, [dispatch, petPaymentResponse])
  //
  //
  useEffect(() => {
    _mainPayment()

    return () => {}
  }, [_mainPayment])

  //
  const fakePayment = async () => {
    if (checkout?.orderId && !isProduction) {
      //
      try {
        //
        await afterPaymentStepsOnDev({
          orderId: checkout?.orderId,
          amount: checkout?.totalPrice,
        }).unwrap()
        //
        toast.success(`${t('paymentSuccessfully')}`)
        //
        // clear abroad rtk store
        dispatch(clearAbroadStore())
        //
        navigate('/pay/payment-success')
        //
      } catch (error: any) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0]
          const errorMessage = firstError.message
          toast.info(errorMessage)
          //
        } else {
          toast.error('შეცდომა გადახდის გვერდზე, Payment.tsx')
        }
      }
      //
    }
  }
  //
  // disable browser back button
  useEffect(() => {
    const disableBackButton = (e: any) => {
      e.preventDefault()
      window.history.forward()
    }

    window.history.pushState('', '', window.location.href)
    window.addEventListener('popstate', disableBackButton)

    return () => {
      window.removeEventListener('popstate', disableBackButton)
    }
  }, [])
  //
  const MainLoading = isPetPaymentUrlLoading || isAfterPaymentStepsOnDevLoading
  //
  return MainLoading ? (
    <Loader text={t('redirectionToPayment')} />
  ) : (
    <Wrapper>
      <Helmet>
        <title>{t('chooseCard')}</title>
        <meta name="description" content={t('chooseCard')} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <BaseModal isOpen={startOverModal} onClose={() => setStartOverModal(!startOverModal)}>
        <StartOverDiv>
          <h5>{t('areyouSureTostartOver')}</h5>
          <ButtonDiv>
            <Button
              fullWidth={true}
              text={t('yes')}
              buttonType="primary"
              onClick={() => [dispatch(clearPetStore()), navigate('/choose-pet')]}
            />
            <Button
              fullWidth={true}
              text={t('no')}
              buttonType="transparent"
              onClick={() => setStartOverModal(!startOverModal)}
            />
          </ButtonDiv>
        </StartOverDiv>
      </BaseModal>

      <ScreenHeader
        rightSideChildren={<StartOver text={t('startOver')} onClick={() => setStartOverModal(!startOverModal)} />}
      />

      <Container>
        <Content>
          <Title text={t('chooseCard')} />

          <PetProgressbar stepName={'Contact Info'} />

          <MovingPrice price={filter?.offerTotalPrice} currency={filter?.offer?.currency} />

          <Cards>
            {['master', 'visa'].map((item, index) => {
              return (
                <PaymentCard
                  key={index}
                  cardName={item === 'master' ? 'Mastercard' : 'Visa'}
                  selected={item === selectedPaymentMethodState}
                  cardType={item}
                  onClick={() => {
                    if (selectedPaymentMethodState === item) {
                      setSelectedPaymentMethodState(item)
                    } else {
                      // always other way
                      setSelectedPaymentMethodState(item)
                      dispatch(setSubscriptionAgree(false))
                    }
                  }}
                  subscriptionCheckbox={
                    item === selectedPaymentMethodState && filter.seletedPaymentMethod?.type === 'MONTHLY'
                  }
                  subscriptionValue={checkout?.subscriptionAgree}
                  onChecboxClick={() => dispatch(setSubscriptionAgree(!checkout?.subscriptionAgree))}
                />
              )
            })}

            {selectedPaymentMethodState &&
            checkout &&
            checkout.orderId &&
            (filter.seletedPaymentMethod?.type === 'MONTHLY' ? checkout?.subscriptionAgree : true) ? (
              <Button
                fullWidth={false}
                style={{
                  marginTop: '48px',
                }}
                text={t('pay')}
                buttonType="primary"
                onClick={() => {
                  // main payment
                  if (checkout && checkout.orderId && isProduction) {
                    _getPetPaymentUrl()
                  }
                  // fake payment
                  if (checkout && checkout.orderId && !isProduction) {
                    fakePayment()
                  }
                }}
              />
            ) : null}
          </Cards>
        </Content>
      </Container>
      <BackgroundIcon />
    </Wrapper>
  )
}
//
//
const Cards = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin-block: 32px;
`
const ButtonDiv = styled.div`
  margin-top: 50px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: 1fr;
  grid-column-gap: 20px;
  grid-row-gap: 0px;

  ${Responsive.mobile} {
    grid-template-columns: repeat(2, 130px);
    justify-content: center;
  }
`
const StartOverDiv = styled.div`
  h5 {
    text-align: center;
    color: ${Colors.primaryBlack};
  }
`
const Content = styled.div`
  padding-block: 60px;
  position: sticky;
  z-index: 1;
  width: 100%;

  ${Responsive.mobile} {
    padding-block: 30px;
  }
`
const Wrapper = styled.div`
  /*  */
`
//
export default PetPayment
